import React, { useState } from 'react';
import { Field, FormSpy } from 'react-final-form';
import styles from '../../Form.module.scss';
import ReactSelectAdapter from './ReactSelectAdapter';
import { usernameSources } from '../../../Utils';
import SearchAndReplace from './SearchAndReplace';
import CleverSchool from './CleverSchool';

const FormRow = ({ children }: { children: ReactChildren }) => (
  <div className={styles.row}>
    <div className={styles.fieldContainer}>
      {children}
    </div>
  </div>
);

interface UsernameSourceProps {
  autoRosterDistrict?: {
    staff_username_source: string;
    username_source: string;
    student_username_search_for: string;
    staffer_username_search_for: string;
    student_username_replace_with: string;
    staffer_username_replace_with: string;
    clever_school_uid?: string;
  }
  rosteringType: 'clever' | 'oneroster';
  subscriberId?: number | string;
}

const UsernameSource = ({ autoRosterDistrict, rosteringType, subscriberId }: UsernameSourceProps) => {
  const [staffUsernameSource, setStaffUsernameSource] = useState(autoRosterDistrict?.staff_username_source);
  const [usernameSource, setUsernameSource] = useState(autoRosterDistrict?.username_source);
  const isClever = rosteringType === 'clever';
  const savedSchoolUid = autoRosterDistrict?.clever_school_uid;
  const initiallyOpen = (
    typeof staffUsernameSource === 'string'
    || typeof usernameSource === 'string'
    || (isClever && typeof savedSchoolUid === 'string')
  );
  const [isOpen, setIsOpen] = useState<boolean>(initiallyOpen);
  const toggleIsOpen = () => setIsOpen(!isOpen);
  const caretClass = isOpen ? 'fa fa-caret-up' : 'fa fa-caret-down';
  const toggleText = isClever
    ? 'Advanced: Set a school or change staff or student username source'
    : 'Advanced: Change staff or student username source';

  return (
    <>
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          if (values.staff_username_source) {
            setStaffUsernameSource(values.staff_username_source);
          }

          if (values.username_source) {
            setUsernameSource(values.username_source);
          }

          return null;
        }}
      </FormSpy>

      <FormRow>
        <button
          className="btn btn--link-purple pl0"
          type="button"
          onClick={toggleIsOpen}
        >
          {toggleText} <i className={caretClass} />
        </button>
      </FormRow>

      {isOpen && (
        <>
          {isClever && (
            <CleverSchool savedSchoolUid={savedSchoolUid} subscriberId={subscriberId} />
          )}

          <FormRow>
            <label
              className={styles.label}
              htmlFor="staff_username_source"
            >
              Staff Username Source
            </label>
            <Field
              className={styles.input}
              component={ReactSelectAdapter}
              id="staff_username_source"
              name="staff_username_source"
              options={usernameSources(rosteringType)}
              parse={val => (val ? val.value : val)}
              placeholder="Select username source"
            />
          </FormRow>

          {!!staffUsernameSource && (
            <SearchAndReplace
              replaceWith={autoRosterDistrict?.staffer_username_replace_with}
              replaceWithFieldName="staffer_username_replace_with"
              searchFor={autoRosterDistrict?.staffer_username_search_for}
              searchForFieldName="staffer_username_search_for"
              userType="staffer"
            />
          )}

          <FormRow>
            <label className={styles.label} htmlFor="username_source">Student Username Source</label>
            <Field
              className={styles.input}
              component={ReactSelectAdapter}
              id="username_source"
              name="username_source"
              options={usernameSources(rosteringType)}
              parse={val => (val ? val.value : val)}
              placeholder="Select username source"
            />
          </FormRow>

          {!!usernameSource && (
            <SearchAndReplace
              replaceWith={autoRosterDistrict?.student_username_replace_with}
              replaceWithFieldName="student_username_replace_with"
              searchFor={autoRosterDistrict?.student_username_search_for}
              searchForFieldName="student_username_search_for"
              userType="student"
            />
          )}
        </>
      )}
    </>
  );
};

export default UsernameSource;
